import { z } from 'zod';

export const DEFAULT_IMAGE_MAX_SIZE_MB = 25;

type ImageFileSchemaOptions = {
  maxSizeMB?: number;
  requiredMessage?: string;
  invalidTypeMessage?: string;
  maxSizeMessage?: string;
};

export const createImageFileSchema = ({
  maxSizeMB = DEFAULT_IMAGE_MAX_SIZE_MB,
  requiredMessage = 'common.form.validation.fileRequired',
  invalidTypeMessage = 'common.form.validation.invalidFileType',
  maxSizeMessage = 'common.form.validation.fileSizeExceeds25MB',
}: ImageFileSchemaOptions = {}) =>
  z
    .instanceof(File, { message: requiredMessage })
    .refine((file) => file.size > 0, { message: requiredMessage })
    .refine((file) => file.type.startsWith('image/'), { message: invalidTypeMessage })
    .refine((file) => file.size <= maxSizeMB * 1024 * 1024, { message: maxSizeMessage });

export const imageFileSchema = createImageFileSchema();

export const fileObjectSchema = z.object({
  file: imageFileSchema,
  id: z.string().trim().min(1, 'common.form.validation.fileRequired'),
  preview: z.string().trim().min(1, 'common.form.validation.fileRequired'),
});

export const existingImageSchema = z.object({
  alt: z.unknown().optional(),
  description: z.string().optional(),
  path: z.string().trim().min(1, 'common.form.validation.required'),
});

export const uploadValueSchema = z.union([
  fileObjectSchema,
  z.string().trim().min(1, 'common.form.validation.required'),
  existingImageSchema,
]);

export const requiredImageValueSchema = z.union([fileObjectSchema, existingImageSchema]);

export const optionalUploadValueSchema = z.union([
  uploadValueSchema,
  fileObjectSchema,
  z.literal(''),
  z.null(),
  z.undefined(),
]);

export const hasUploadValue = (value: unknown): boolean => uploadValueSchema.safeParse(value).success;

export type UploadValue = z.infer<typeof uploadValueSchema>;
export type ImageFileObject = z.infer<typeof fileObjectSchema>;
